export const tokens = {
  colors: {
    brand: {
      deep: { hex: '#001D88', rgb: '0 29 136' },
      blue: { hex: '#1A6FCC', rgb: '26 111 204' },
      mid: { hex: '#2F8FE8', rgb: '47 143 232' },
    },
    secondary: {
      sky: { hex: '#5BB8F5', rgb: '91 184 245' },
      lightSky: { hex: '#A8DCFA', rgb: '168 220 250' },
      iceBlue: { hex: '#D6EEFA', rgb: '214 238 250' },
    },
    neutral: {
      white: { hex: '#FFFFFF', rgb: '255 255 255' },
      offWhite: { hex: '#F4F6F8', rgb: '244 246 248' },
      slate: { hex: '#8A9BAD', rgb: '138 155 173' },
      navy: { hex: '#1C2B3A', rgb: '28 43 58' },
      black: { hex: '#141414', rgb: '20 20 20' },
    },
    accent: {
      paleMist: { hex: '#E8F4FF', rgb: '232 244 255' },
    },
    gray: {
      '50': { hex: '#F7F9FB', rgb: '247 249 251' },
      '100': { hex: '#EDF1F5', rgb: '237 241 245' },
      '200': { hex: '#DDE3EA', rgb: '221 227 234' },
      '300': { hex: '#C4CDD8', rgb: '196 205 216' },
      '400': { hex: '#9AAAB8', rgb: '154 170 184' },
      '500': { hex: '#6B7E90', rgb: '107 126 144' },
      '600': { hex: '#4A5D6E', rgb: '74 93 110' },
      '700': { hex: '#344B5C', rgb: '52 75 92' },
      '800': { hex: '#1C2B3A', rgb: '28 43 58' },
      '900': { hex: '#0E1A24', rgb: '14 26 36' },
    },
    success: {
      foreground: { hex: '#1A7A5E', rgb: '26 122 94' },
      surface: { hex: '#D3F0E8', rgb: '211 240 232' },
    },
    warning: {
      foreground: { hex: '#EA9C26', rgb: '234 156 38' },
      surface: { hex: '#F7EAD6', rgb: '247 234 214' },
    },
    danger: {
      foreground: { hex: '#E04547', rgb: '224 69 71' },
      surface: { hex: '#F8D8D9', rgb: '248 216 217' },
    },
  },

  typography: {
    families: {
      primary: "'Plus Jakarta Sans Variable', sans-serif",
      secondary: "'DM Sans Variable', sans-serif",
      secondaryVariationSettings: "'opsz' 14",
    },
    weights: {
      extrabold: 800,
      bold: 700,
      semibold: 600,
      medium: 500,
      regular: 400,
    },
    headings: {
      h1: { family: 'primary', fontSize: '64px', lineHeight: 'normal' },
      h2: { family: 'primary', fontSize: '60px', lineHeight: 'normal' },
      h3: { family: 'primary', fontSize: '54px', lineHeight: 'normal' },
      h4: { family: 'primary', fontSize: '44px', lineHeight: 'normal' },
      h5: { family: 'primary', fontSize: '40px', lineHeight: 'normal' },
      h6: { family: 'primary', fontSize: '24px', lineHeight: 'normal' },
    },
    supporting: {
      caption: { family: 'primary', fontSize: '18px', lineHeight: 'normal' },
      highlight: { family: 'primary', fontSize: '16px', lineHeight: 'normal' },
      callout: { family: 'primary', fontSize: '14px', lineHeight: 'normal' },
    },
    body: {
      body1: { family: 'secondary', fontSize: '18px', lineHeight: 'normal' },
      body2: { family: 'secondary', fontSize: '16px', lineHeight: 'normal' },
      body3: { family: 'secondary', fontSize: '14px', lineHeight: 'normal' },
    },
    buttonText: {
      buttonLarge: { family: 'primary', fontSize: '20px', lineHeight: 'normal' },
      buttonMid: { family: 'primary', fontSize: '16px', lineHeight: 'normal' },
      buttonSmall: { family: 'primary', fontSize: '14px', lineHeight: 'normal' },
    },
  },

  gridLayout: {
    desktop: { columns: 12, margin: '80px', gutter: '24px', maxWidth: '1440px' },
    mobile: { columns: 4, margin: '16px', gutter: '16px' },
  },

  spacing: {
    '0': '0px',
    '1': '4px',
    '2': '8px',
    '3': '12px',
    '4': '16px',
    '5': '20px',
    '6': '24px',
    '8': '32px',
    '10': '40px',
    '12': '48px',
    '16': '64px',
    '20': '80px',
    'grid-margin-mobile': '16px',
    'grid-margin-desktop': '80px',
    'grid-gutter-mobile': '16px',
    'grid-gutter-desktop': '24px',
  },

  radii: {
    none: '0px',
    xs: '2px',
    sm: '4px',
    md: '8px',
    lg: '16px',
    full: '9999px',
  },

  // Panel-level drop-shadow tokens. Distinct from `button.shadows` (variant-specific
  // primary/hover shadows) — these are reusable container shadows traceable to Figma.
  shadows: {
    dropdown: '0px 4px 64px rgba(28, 43, 58, 0.16)',
  },

  borderWidths: {
    '0': '0px',
    DEFAULT: '1px',
  },

  breakpoints: {
    desktop: '1024px',
  },

  button: {
    sizes: {
      lg: { height: '64px', textStyle: 'buttonLarge' },
      md: { height: '48px', textStyle: 'buttonMid' },
      sm: { height: '40px', textStyle: 'buttonMid' },
      xs: { height: '32px', textStyle: 'buttonSmall' },
    },
    variants: {
      primary: { allowedWeights: ['bold', 'semibold'] },
      secondary: { allowedWeights: ['bold', 'medium'] },
    },
    // Drop-shadow values traceable to Figma's Primary button shadow definitions.
    // Reuses palette tokens via CSS vars so a future palette change flows through automatically.
    shadows: {
      primaryDefault: '0px 4px 15px 0px rgb(var(--color-secondary-sky) / 0.32)',
      primaryHover: '0px 4px 30px 0px rgb(var(--color-secondary-light-sky) / 0.30)',
    },
  },

  meta: {
    provisionalCategories: ['spacing'],
    openQuestions: [
      'Spacing scale: keep half-steps (4 / 12 / 20) for fine-grained control, or strict 8px multiples only? Implementation keeps half-steps pending designer ratification.',
    ],
  },
} as const;

export type DesignTokens = typeof tokens;

export type ColorToken = {
  hex: `#${string}`;
  rgb: string;
  isProvisional?: boolean;
};

export type ButtonVariantKey = keyof DesignTokens['button']['variants'];
export type ButtonSizeKey = keyof DesignTokens['button']['sizes'];
export type FontFamilyKey = 'primary' | 'secondary';
export type FontWeightKey = keyof DesignTokens['typography']['weights'];
